"use client";

import {
  Button,
  Card,
  Heading,
  Inline,
  Stack,
  Text,
} from "@softmax/design-system";
import {
  PolicyDivisionStanding,
  type PolicyDivisionStandingProps,
} from "./policy-division-standing";
import {
  PolicyVersionScore,
  type PolicyVersionScoreProps,
} from "./policy-version-score";
import {
  PolicyVersionSubmissions,
  type PolicyVersionSubmissionsProps,
} from "./policy-version-submissions";
import {
  PolicyStatusHistoryPanel,
  type PolicyStatusHistoryPanelProps,
} from "./policy-status-history-panel";
import type { ReactElement, ReactNode } from "react";

export type PolicyVersionOverviewProps = Pick<
  PolicyDivisionStandingProps,
  "placements" | "onOpenLeague"
> & {
  readonly section?:
    | "overview"
    | "activity"
    | "episodes"
    | "access"
    | "diagnostics"
    | undefined;
  readonly player: { readonly id: string; readonly name: string } | null;
  readonly latestStatus: {
    readonly status: string;
    readonly substatus: string | null;
  } | null;
  readonly progression: ReactNode;
  readonly onRetry?: (() => void) | undefined;
  readonly score: PolicyVersionScoreProps["score"];
  readonly submissions: PolicyVersionSubmissionsProps["submissions"];
  readonly statusHistory: PolicyStatusHistoryPanelProps["statusHistory"];
  /** The version's recent episode requests, loaded independently. */
  readonly recentEpisodes: ReactElement;
  readonly onOpenPlayer: (id: string) => void;
  readonly onOpenStatusSource: (key: string) => void;
};

export function PolicyVersionOverview(props: PolicyVersionOverviewProps) {
  const {
    player,
    latestStatus,
    score,
    placements,
    submissions,
    statusHistory,
  } = props;
  return (
    <Stack width="auto" gap={24}>
      <div hidden={props.section !== undefined && props.section !== "overview"}>
        <Stack width="auto" gap={24}>
          {(player || latestStatus) && (
            <Inline>
              {player && (
                <Button
                  size="sm"
                  variant="quiet"
                  onClick={() => props.onOpenPlayer(player.id)}
                >
                  {player.name}
                </Button>
              )}
              {latestStatus && (
                <Text>
                  Latest status: {latestStatus.status} {latestStatus.substatus}
                </Text>
              )}
            </Inline>
          )}
          {props.progression}
          <PolicyVersionScore score={score} onRetry={props.onRetry} />
          <PolicyDivisionStanding
            placements={placements}
            onRetry={props.onRetry}
            onOpenLeague={props.onOpenLeague}
          />
        </Stack>
      </div>
      <div hidden={props.section !== undefined && props.section !== "activity"}>
        <Stack width="auto" gap={24}>
          <PolicyVersionSubmissions
            submissions={submissions}
            onRetry={props.onRetry}
            onOpenLeague={props.onOpenLeague}
          />
          <PolicyStatusHistoryPanel
            statusHistory={statusHistory}
            onRetry={props.onRetry}
            onOpenStatusSource={props.onOpenStatusSource}
          />
        </Stack>
      </div>
      <div hidden={props.section !== undefined && props.section !== "episodes"}>
        <Card>
          <Stack width="auto">
            <Heading level={2}>Recent episodes</Heading>
            {props.recentEpisodes}
          </Stack>
        </Card>
      </div>
    </Stack>
  );
}
